import { SERPEvent, SERPEventType } from '~/core/events/types/SERPEvents';

export interface EventQueue {
    addEvent(event: SERPEvent): void;
    processQueue(): void;
    clearQueue(): void;
}

export type EventHandler = (event: SERPEvent) => Promise<void>;

/**
 * EventQueue is a queue of captured events that are waiting to be sent to some external service.
 * It is used to store events that are not ment to be sent to external service immediately, but rather accumullated and with small delay.
 * The purpose is to accumulate search events and send them in batches - not sending each letter separately, but rather
 * sending a whole word when user stops typing.
 * If there is an event redirecting user to another page, the queue should be processed.
 * If there is an event closing the SERP, the queue should be processed.
 * If there are waiting events, process queue in passed interval (default 5 seconds)
 */
export class EventQueue implements EventQueue {
    private eventHandler: EventHandler;
    private queue: SERPEvent[] = [];
    private timeout: ReturnType<typeof setTimeout> | undefined;
    private readonly delay: number;

    constructor(handler: EventHandler, delay = 3000) {
        this.eventHandler = handler;
        this.delay = delay;
    }

    public clearTimeout(): void {
        window.clearTimeout(this.timeout);
    }

    public addEvent(event: SERPEvent): void {
        this.queue.push(event);
        window.clearTimeout(this.timeout);
        this.timeout = setTimeout(() => this.processQueue(), this.delay);
    }

    public getQueue(): SERPEvent[] {
        return this.queue;
    }

    public clearQueue(): void {
        this.queue = [];
        this.clearTimeout();
    }

    public async processQueue(): Promise<void> {
        if (this.queue.length === 0) {
            return;
        }

        const reduced = this.reduceQueue(this.queue);

        const queueDelivery = reduced.map((event) => this.eventHandler(event));
        await Promise.allSettled(queueDelivery);

        this.clearQueue();
    }

    public reduceQueue(queue: SERPEvent[]): SERPEvent[] {
        return queue.reduce((acc, current, currentIndex) => {
            // If current event is last in queue, always add it to final queue
            const isLastAction = currentIndex === queue.length - 1;
            if (isLastAction) {
                return [...acc, current];
            }

            // If current event is search, check if next event is search and if it is a substring of current search query
            if (current.type === SERPEventType.SEARCH) {
                // If current event is search, check if next event is search
                const next = queue[currentIndex + 1];
                if (next.type !== SERPEventType.SEARCH) {
                    return [...acc, current];
                }
                // If current search query is a substring of next search query, skip it
                if (next.detail.data.searchQuery.includes(current.detail.data.searchQuery)) {
                    return [...acc];
                }

                // If next search query is a substring of current search query, skip it - prevent bakspaces
                if (current.detail.data.searchQuery.includes(next.detail.data.searchQuery)) {
                    return [...acc];
                }
            }

            // if event is not search, always add it to final queue
            return [...acc, current];
        }, [] as SERPEvent[]);
    }
}
